import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useGetCountriesQuery, useGetStatesQuery, type Country } from '@/redux/internal-services/geoApi';
import { cn } from '@/utils/cn';
import { fieldClass } from './fieldStyles';

const cls = (...args: (string | false | undefined | null)[]) => args.filter(Boolean).join(' ');

// ─── Shared dropdown shell ────────────────────────────────────────────────────

interface DropdownShellProps {
  display: string;
  placeholder: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLDivElement>;
}

const DropdownShell: React.FC<DropdownShellProps> = ({
  display, placeholder, open, onToggle, children, containerRef,
}) => (
  <div ref={containerRef} className="relative">
    <button
      type="button"
      onClick={onToggle}
      className={cn(fieldClass(), 'flex items-center justify-between text-left')}
    >
      <span className={cn('min-w-0 truncate', display ? 'text-ui-text' : 'text-ui-text3')}>
        {display || placeholder}
      </span>
      <svg
        className={cls('h-4 w-4 text-ui-text3 shrink-0 transition-transform', open && 'rotate-180')}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {open && (
      <div className="absolute z-50 mt-1 min-w-[200px] w-full bg-ui-card border border-ui-border rounded-card shadow-pop overflow-hidden">
        {children}
      </div>
    )}
  </div>
);

// ─── CountrySelect ────────────────────────────────────────────────────────────

interface CountrySelectProps {
  value: string;       // country code e.g. "US"
  onChange: (code: string, name: string, timezone?: string) => void;
  // Fired when the incoming value resolves to a country but isn't the ISO code
  // (e.g. legacy data stored the name). Lets the parent backfill country_code
  // without triggering side effects like timezone auto-derivation.
  onResolve?: (code: string, name: string) => void;
  displayField?: 'name' | 'dial_code';
  placeholder?: string;
  className?: string;
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  onResolve,
  displayField = 'name',
  placeholder = 'Select country',
  className,
}) => {
  const { data, isLoading } = useGetCountriesQuery();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const countries = data?.data ?? [];
  const priority = useMemo(() => countries.filter((c) => c.priority < 99), [countries]);
  const rest = useMemo(
    () => countries.filter((c) => c.priority === 99).sort((a, b) => a.name.localeCompare(b.name)),
    [countries]
  );

  const filtered = useMemo(() => {
    if (!query) return null;
    const q = query.toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial_code.includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [query, countries]);

  // Match by code (case-insensitive) or name — saved value may be either, and
  // legacy/imported data sometimes stores the country name or a lower-cased code.
  const selected = useMemo(() => {
    if (!value) return undefined;
    const v = value.toLowerCase();
    return countries.find((c) => c.code.toLowerCase() === v || c.name.toLowerCase() === v);
  }, [countries, value]);

  // Backfill the canonical ISO code when the stored value was a name or a
  // mis-cased code, so dependent fields (e.g. StateSelect) get a real code.
  useEffect(() => {
    if (onResolve && selected && selected.code !== value) {
      onResolve(selected.code, selected.name);
    }
  }, [selected, value, onResolve]);
  const display = selected
    ? displayField === 'dial_code'
      ? `${selected.dial_code} ${selected.name}`
      : selected.name
    : '';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setShowAll(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    setOpen((v) => !v);
    if (!open) {
      setQuery('');
      setShowAll(false);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const handleSelect = (c: Country) => {
    onChange(c.code, c.name, c.timezone);
    setOpen(false);
    setQuery('');
    setShowAll(false);
  };

  const renderItem = (c: Country) => (
    <li
      key={c.code}
      onMouseDown={() => handleSelect(c)}
      className={cls(
        'px-4 py-2 text-sm cursor-pointer hover:bg-ui-hover flex items-center justify-between',
        c.code === value ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text'
      )}
    >
      <span>{c.name}</span>
      {displayField === 'dial_code' && (
        <span className="text-xs text-ui-text3 ml-2">{c.dial_code}</span>
      )}
    </li>
  );

  return (
    <div className={className}>
      <DropdownShell
        display={display}
        placeholder={isLoading ? 'Loading...' : placeholder}
        open={open}
        onToggle={handleToggle}
        containerRef={containerRef as React.RefObject<HTMLDivElement>}
      >
        {/* Search */}
        <div className="p-2 border-b border-ui-border">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search country..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowAll(true); }}
            className={fieldClass({ size: 'sm' })}
          />
        </div>

        <ul className="max-h-56 overflow-y-auto">
          {filtered !== null ? (
            filtered.length === 0
              ? <li className="px-4 py-2 text-sm text-ui-text3">No results</li>
              : filtered.map(renderItem)
          ) : showAll ? (
            // Full alphabetical list
            [...countries].sort((a, b) => a.name.localeCompare(b.name)).map(renderItem)
          ) : (
            <>
              {/* Priority countries only */}
              {priority.map(renderItem)}
              {rest.length > 0 && (
                <li
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowAll(true); }}
                  className="px-4 py-2 text-sm text-accent-text font-medium cursor-pointer hover:bg-ui-hover border-t border-ui-border flex items-center gap-1.5"
                >
                  <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                  </svg>
                  Show all countries ({rest.length} more)
                </li>
              )}
            </>
          )}
        </ul>
      </DropdownShell>
    </div>
  );
};

// ─── DialCodeSelect ───────────────────────────────────────────────────────────

interface DialCodeSelectProps {
  value: string;    // dial_code e.g. "+1"
  onChange: (dialCode: string) => void;
  className?: string;
}

export const DialCodeSelect: React.FC<DialCodeSelectProps> = ({ value, onChange, className }) => {
  const { data, isLoading } = useGetCountriesQuery();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const countries = data?.data ?? [];
  const priority = useMemo(() => countries.filter((c) => c.priority < 99), [countries]);
  const rest = useMemo(
    () => countries.filter((c) => c.priority === 99).sort((a, b) => a.name.localeCompare(b.name)),
    [countries]
  );

  const filtered = useMemo(() => {
    if (!query) return null;
    const q = query.toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial_code.includes(q)
    );
  }, [query, countries]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setShowAll(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    setOpen((v) => !v);
    if (!open) {
      setQuery('');
      setShowAll(false);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const handleSelect = (c: Country) => {
    onChange(c.dial_code);
    setOpen(false);
    setQuery('');
    setShowAll(false);
  };

  const renderItem = (c: Country) => (
    <li
      key={c.code}
      onMouseDown={() => handleSelect(c)}
      className={cls(
        'px-3 py-2 text-sm cursor-pointer hover:bg-ui-hover flex items-center gap-1',
        c.dial_code === value ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text'
      )}
    >
      <span className="w-8 shrink-0 font-mono text-xs">{c.dial_code}</span>
      <span className="truncate">{c.name}</span>
    </li>
  );

  if (!isLoading && countries.length < 2) return null;

  return (
    <div className={className}>
      <DropdownShell
        display={value}
        placeholder={isLoading ? '...' : '+1'}
        open={open}
        onToggle={handleToggle}
        containerRef={containerRef as React.RefObject<HTMLDivElement>}
      >
        <div className="p-2 border-b border-ui-border">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search country or code..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={fieldClass({ size: 'sm' })}
          />
        </div>
        <ul className="max-h-56 overflow-y-auto">
          {filtered !== null ? (
            filtered.length === 0
              ? <li className="px-4 py-2 text-sm text-ui-text3">No results</li>
              : filtered.map(renderItem)
          ) : showAll ? (
            [...countries].sort((a, b) => a.name.localeCompare(b.name)).map(renderItem)
          ) : (
            <>
              {priority.map(renderItem)}
              {rest.length > 0 && (
                <li
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowAll(true); }}
                  className="px-4 py-2 text-sm text-accent-text font-medium cursor-pointer hover:bg-ui-hover border-t border-ui-border flex items-center gap-1.5"
                >
                  <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
                  </svg>
                  Show all countries ({rest.length} more)
                </li>
              )}
            </>
          )}
        </ul>
      </DropdownShell>
    </div>
  );
};

// ─── StateSelect ──────────────────────────────────────────────────────────────

interface StateSelectProps {
  countryCode: string;
  value: string;
  onChange: (code: string, name: string, timezone?: string) => void;
  placeholder?: string;
  className?: string;
}

export const StateSelect: React.FC<StateSelectProps> = ({
  countryCode,
  value,
  onChange,
  placeholder = 'Select state / province',
  className,
}) => {
  const { data, isLoading } = useGetStatesQuery(countryCode, { skip: !countryCode });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const states = data?.data ?? [];
  const filtered = useMemo(() => {
    if (!query) return states;
    const q = query.toLowerCase();
    return states.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));
  }, [query, states]);

  // Match by code or name — stored value may be either (legacy data stored the name).
  const selected = states.find((s) => s.code === value || s.name === value);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset only when the user actually changes country — not on initial mount,
  // otherwise the saved state value gets wiped as soon as the form loads.
  const prevCountry = useRef(countryCode);
  useEffect(() => {
    // Skip the empty → code transition: that's the parent backfilling the
    // canonical country_code for legacy data, not a real user country change.
    if (prevCountry.current !== countryCode) {
      const wasBackfill = !prevCountry.current && !!countryCode;
      prevCountry.current = countryCode;
      if (value && !wasBackfill) onChange('', '');
    }
  }, [countryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => {
    if (!countryCode) return;
    setOpen((v) => !v);
    if (!open) {
      setQuery('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const hasStates = states.length > 0;

  // Fall back to text input when no states seeded for this country
  if (!isLoading && countryCode && !hasStates) {
    return (
      <input
        type="text"
        placeholder="State / Province"
        value={value}
        onChange={(e) => onChange(e.target.value, e.target.value)}
        className={cls(
          fieldClass(),
          className
        )}
      />
    );
  }

  return (
    <div className={className}>
      <DropdownShell
        display={selected?.name ?? value}
        placeholder={
          !countryCode ? 'Select country first' :
          isLoading ? 'Loading...' :
          placeholder
        }
        open={open}
        onToggle={handleToggle}
        containerRef={containerRef as React.RefObject<HTMLDivElement>}
      >
        <div className="p-2 border-b border-ui-border">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search state..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={fieldClass({ size: 'sm' })}
          />
        </div>
        <ul className="max-h-52 overflow-y-auto">
          {filtered.length === 0
            ? <li className="px-4 py-2 text-sm text-ui-text3">No results</li>
            : filtered.map((s) => (
                <li
                  key={s.code}
                  onMouseDown={() => { onChange(s.code, s.name, s.timezone); setOpen(false); setQuery(''); }}
                  className={cls(
                    'px-4 py-2 text-sm cursor-pointer hover:bg-ui-hover',
                    s.code === selected?.code ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text'
                  )}
                >
                  {s.name}
                </li>
              ))
          }
        </ul>
      </DropdownShell>
    </div>
  );
};

// ─── TimezoneSelect ───────────────────────────────────────────────────────────

interface TimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  placeholder?: string;
  className?: string;
}

function formatTimezone(tz: string): string {
  try {
    // Compute the zone's true UTC offset by rendering the same instant in the target zone and in
    // UTC, then parsing BOTH back as browser-local time. The browser's own offset is applied to
    // each parse identically, so it cancels out — leaving only tz's offset. (The previous version
    // subtracted a raw epoch from a browser-local parse, which folded the browser's offset into the
    // result: on an IST +5:30 machine every zone read ~5:30 too negative, e.g. "GMT-09:30 New York".)
    const now = new Date();
    const tzMs = new Date(now.toLocaleString('en-US', { timeZone: tz })).getTime();
    const utcMs = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
    const offsetMin = Math.round((tzMs - utcMs) / 60000);
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
    return `(GMT${sign}${hh}:${mm}) ${city}`;
  } catch {
    return tz.replace(/_/g, ' ');
  }
}

export const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  placeholder = 'Select timezone',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: statesData } = useGetStatesQuery('US');

  // Derive unique timezones from US states, formatted as (GMT±HH:MM) City.
  const timezoneOptions = useMemo(() => {
    const states = statesData?.data ?? [];
    const seen = new Set<string>();
    const options: { tz: string; label: string }[] = [];
    for (const s of states) {
      if (s.timezone && !seen.has(s.timezone)) {
        seen.add(s.timezone);
        options.push({ tz: s.timezone, label: formatTimezone(s.timezone) });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [statesData]);

  const filtered = useMemo(() => {
    if (!query) return timezoneOptions;
    const q = query.toLowerCase();
    return timezoneOptions.filter(({ tz, label }) => tz.toLowerCase().includes(q) || label.toLowerCase().includes(q));
  }, [query, timezoneOptions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    setOpen((v) => !v);
    if (!open) {
      setQuery('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const selectedLabel = useMemo(
    () => (value ? (timezoneOptions.find((o) => o.tz === value)?.label ?? formatTimezone(value)) : ''),
    [value, timezoneOptions],
  );

  return (
    <div className={className}>
      <DropdownShell
        display={selectedLabel}
        placeholder={placeholder}
        open={open}
        onToggle={handleToggle}
        containerRef={containerRef as React.RefObject<HTMLDivElement>}
      >
        <div className="p-2 border-b border-ui-border">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search timezone..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={fieldClass({ size: 'sm' })}
          />
        </div>
        <ul className="max-h-52 overflow-y-auto">
          {filtered.length === 0
            ? <li className="px-4 py-2 text-sm text-ui-text3">No results</li>
            : filtered.map(({ tz, label }) => (
                <li
                  key={tz}
                  onMouseDown={() => { onChange(tz); setOpen(false); setQuery(''); }}
                  className={cls(
                    'px-4 py-2 text-sm cursor-pointer hover:bg-ui-hover',
                    tz === value ? 'bg-accent-soft text-accent-text font-medium' : 'text-ui-text'
                  )}
                >
                  {label}
                </li>
              ))
          }
        </ul>
      </DropdownShell>
    </div>
  );
};
